import { useState, useEffect, useRef, useCallback, memo } from "react";
import { Circle, Group, Layer, Line, Stage, Text } from "react-konva";
import nipplejs from "nipplejs";

const ExerciseCanvas = ({ zone, isEditing, exercises = [], onZoneChange, transform = { stageScale: 1, stageX: 0, stageY: 0 }, metrics = { videoDomWidth: 0, videoDomHeight: 0, videoWidth: 0, videoHeight: 0, ratio: 1, imageY: 0 } }) => {
  const [currentZone, setCurrentZone] = useState([]);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [allPoints, setAllPoints] = useState([]);
  const [active, setActive] = useState(null);
  const [stick, setStick] = useState({ x: 0, y: 0 });
  const stageRef = useRef();
  const layerRef = useRef();
  const containerRef = useRef();
  const nippleRef = useRef();
  const managerRef = useRef();
  const forwardingDragRef = useRef(false);

  const container = containerRef.current;

  // Constants (similar to FieldCanvas)
  const defaultWidth = 1030;
  const defaultRadius = 8;
  const defaultStickForce = 0.05;

  const nippleOptions = {
    color: "#ffffff",
    size: 100,
    mode: "dynamic",
  };

  // Predefined exercise points (only 2 points: top-left and bottom-right)
  const defaultFieldPoints = [
    { id: "0", x: 0.2, y: 0.2, color: "#6200EE", desc: "Top Left" },
    { id: "1", x: 0.8, y: 0.8, color: "#FFDE03", desc: "Bottom Right" },
  ];

  // Initialize points when component mounts or when editing existing exercise
  useEffect(() => {
    if (zone && zone.length === 2) {
      // Use existing exercise points when editing
      const exercisePoints = zone.map((point, index) => ({
        id: point.id || index.toString(),
        x: point.x,
        y: point.y,
        color: index === 0 ? "#6200EE" : "#FFDE03",
        desc: index === 0 ? "Top Left" : "Bottom Right",
      }));
      setAllPoints(exercisePoints);
      // Auto-select the existing points when editing
      setSelectedPoints(exercisePoints.map((p) => p.id));
    } else if (zone && zone.length === 0) {
      // Only reset when zone is explicitly empty (new exercise)
      setAllPoints(defaultFieldPoints);
      console.log("defaultFieldPoints", defaultFieldPoints);
      setSelectedPoints([]);
    }
    // Don't reset when zone has 1 point (in progress)
  }, [zone]);

  // Sync current zone with parent
  useEffect(() => {
    setCurrentZone(zone || []);
  }, [zone]);

  // Move point function (like FieldCanvas)
  const movePoint = useCallback(() => {
    if (!active) return;

    const points = [...allPoints];
    const pointIndex = points.findIndex((point) => point.id === active);
    if (pointIndex === -1) return;

    const point = points[pointIndex];
    let { x, y } = point;
    x = +(x + stick.x);
    y = +(y + stick.y);

    // Ensure coordinates stay within bounds (0-1)
    x = Math.max(0, Math.min(1, x));
    y = Math.max(0, Math.min(1, y));

    point.x = x;
    point.y = y;
    points[pointIndex] = point;

    setAllPoints(points);

    // Update current zone if this point is selected
    if (selectedPoints.includes(active)) {
      const updatedZone = currentZone.map((p) =>
        p.id === active ? { ...p, x, y } : p
      );
      setCurrentZone(updatedZone);
      onZoneChange(updatedZone);
    }
  }, [active, stick, allPoints, selectedPoints, currentZone, onZoneChange]);

  // Stick listener (like FieldCanvas)
  const stickListener = useCallback(
    (manager) => {
      let interval;
      manager.on("added", () => {
        interval = setInterval(() => {
          movePoint();
        }, 25);
      });
      manager.on("move", (e, stickData) => {
        const container = containerRef.current;
        if (!container) return;

        const offsetY = +(
          ((stickData.distance * Math.sin(stickData.angle.radian)) /
            container.offsetHeight) *
          defaultStickForce *
          -1
        );
        const offsetX = +(
          ((stickData.distance * Math.cos(stickData.angle.radian)) /
            container.offsetWidth) *
          defaultStickForce
        );

        setStick({ x: offsetX, y: offsetY });
      });
      manager.on("end", () => {
        clearInterval(interval);
        setStick({ x: 0, y: 0 });
      });
    },
    [movePoint, defaultStickForce]
  );

  const handlePointClick = useCallback(
    (pointId) => {
      if (!isEditing) return;

      // Set active point (like FieldCanvas)
      setActive(pointId);

      setSelectedPoints((prev) => {
        let newSelected;
        console.log("handlePointClick prev", prev);

        if (prev.includes(pointId)) {
          // Remove if already selected
          newSelected = prev.filter((id) => id !== pointId);
        } else {
          // Add if not selected, but limit to exactly 2 points
          if (prev.length >= 2) {
            // Replace the first point with the new one
            newSelected = [prev[1], pointId];
          } else {
            newSelected = [...prev, pointId];
          }
        }

        // Update current zone with selected points
        const zonePoints = newSelected.map((id) => {
          const point = allPoints.find((p) => p.id === id);
          return {
            id: point.id,
            x: point.x,
            y: point.y,
          };
        });

        setCurrentZone(zonePoints);
        console.log("handle PointClick", zonePoints);
        onZoneChange(zonePoints);

        return newSelected;
      });
    },
    [isEditing, allPoints, onZoneChange]
  );

  const handlePointDragStart = useCallback(
    (pointId, e) => {
      if (!isEditing) return;

      // Update dragging state for the point
      setAllPoints((prev) =>
        prev.map((p) => ({
          ...p,
          isDragging: p.id === pointId,
        }))
      );
      setActive(pointId);

      // If the point is not in selectedPoints, add it to selection
      setSelectedPoints((prev) => {
        console.log("prev", prev);
        if (prev.includes(pointId)) {
          return prev; // Already selected
        }

        // Add to selection, but limit to exactly 2 points
        let newSelected;
        if (prev.length >= 2) {
          // Replace the first point with the new one
          newSelected = [prev[1], pointId];
        } else {
          newSelected = [...prev, pointId];
        }

        // Update current zone with selected points
        const zonePoints = newSelected.map((id) => {
          const point = allPoints.find((p) => p.id === id);
          return {
            id: point.id,
            x: point.x,
            y: point.y,
          };
        });

        setCurrentZone(zonePoints);
        console.log("handle PointDragStart", zonePoints);
        onZoneChange(zonePoints);

        return newSelected;
      });
    },
    [isEditing, allPoints, onZoneChange]
  );

  const handlePointDragMove = useCallback(
    (pointId, e) => {
      if (!isEditing) return;

      const stage = stageRef.current;
      if (!stage) return;

      // Reset dragged node visual offset (we compute absolute pos ourselves)
      e.target.x(0);
      e.target.y(0);

      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      // Convert from stage coordinates to content coordinates (inverse transform)
      const contentX = (pointer.x - (transform.stageX || 0)) / (transform.stageScale || 1);
      const contentY = (pointer.y - (transform.stageY || 0)) / (transform.stageScale || 1);

      // Remove ratio and imageY to get raw video pixel coordinates
      const videoPixelX = (metrics.ratio ? contentX / metrics.ratio : 0);
      const videoPixelY = (metrics.ratio ? (contentY - (metrics.imageY || 0)) / metrics.ratio : 0);

      // Normalize 0..1 within intrinsic video dimensions
      const relativeX = Math.max(0, Math.min(1, +(metrics.videoWidth ? videoPixelX / metrics.videoWidth : 0)));
      const relativeY = Math.max(0, Math.min(1, +(metrics.videoHeight ? videoPixelY / metrics.videoHeight : 0)));

      // Update the point in allPoints
      setAllPoints((prev) =>
        prev.map((p) =>
          p.id === pointId ? { ...p, x: relativeX, y: relativeY } : p
        )
      );

      // Update current zone if this point is selected
      if (selectedPoints.includes(pointId)) {
        setCurrentZone((prev) =>
          prev.map((p) =>
            p.id === pointId ? { ...p, x: relativeX, y: relativeY } : p
          )
        );
        const updatedZone = currentZone.map((p) =>
          p.id === pointId ? { ...p, x: relativeX, y: relativeY } : p
        );
        console.log("handle PointDragMove", updatedZone);
        onZoneChange(updatedZone);
      }
    },
    [isEditing, selectedPoints, currentZone, onZoneChange, transform, metrics]
  );

  const handlePointDragEnd = useCallback(
    (pointId, e) => {
      if (!isEditing) return;

      // Clear dragging state
      setAllPoints((prev) =>
        prev.map((p) => ({
          ...p,
          isDragging: false,
        }))
      );
    },
    [isEditing]
  );

  const clearZone = useCallback(() => {
    setSelectedPoints([]);
    setCurrentZone([]);
    onZoneChange([]);
  }, [onZoneChange]);

  // Canvas render function to update Stage dimensions
  const canvasRender = useCallback(() => {
    const stage = stageRef.current;
    const container = containerRef.current;
    if (stage && container) {
      stage.width(container.offsetWidth);
      stage.height(container.offsetHeight);
    }
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleWindowResize = () => {
      canvasRender();
    };

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [canvasRender]);

  // Handle keyboard events (like FieldCanvas)
  useEffect(() => {
    const handleCanvasKeyPress = (e) => {
      switch (e.key) {
        case "Escape":
          setActive(null);
          break;
        default:
          break;
      }
    };

    if (isEditing) {
      document.addEventListener("keyup", handleCanvasKeyPress);
      return () => {
        document.removeEventListener("keyup", handleCanvasKeyPress);
      };
    }
  }, [isEditing]);

  // Initialize nipplejs (like FieldCanvas)
  useEffect(() => {
    if (isEditing && nippleRef.current && !managerRef.current) {
      managerRef.current = nipplejs.create({
        ...nippleOptions,
        zone: nippleRef.current,
      });
      stickListener(managerRef.current);
    }

    return () => {
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
    };
  }, [isEditing, nippleOptions, stickListener]);

  // Initial canvas render and when container changes
  useEffect(() => {
    canvasRender();
  }, [canvasRender]);

  const activePoint = active && allPoints.find((point) => point.id === active);

  // Use the same coordinate system as FieldCanvas
  const width = container ? container.offsetWidth : 0;
  const height = container ? container.offsetHeight : 0;
  const actualRadius = container
    ? (width / defaultWidth) * defaultRadius
    : defaultRadius;

  // Helper functions like FieldCanvas
  // Map normalized [0..1] video coords to stage content coords
  const toAbsX = (x) => (x * (metrics.videoWidth || 0)) * (metrics.ratio || 1);
  const toAbsY = (y) => (y * (metrics.videoHeight || 0)) * (metrics.ratio || 1) + (metrics.imageY || 0);

  const handleWheelForwardToPlayer = useCallback((e) => {
    // Only zoom when Ctrl is pressed; otherwise, ignore
    if (!e.evt || !e.evt.ctrlKey) return;
    e.evt.preventDefault();
    e.evt.stopPropagation();
    try {
      const underlying = document.querySelector('.video-player-stage');
      if (!underlying) return;
      const konvaContent = underlying.querySelector('.konvajs-content') || underlying;
      const original = e.evt;
      const wheelEvent = new WheelEvent('wheel', {
        deltaY: original.deltaY,
        deltaX: original.deltaX,
        deltaMode: original.deltaMode,
        clientX: original.clientX,
        clientY: original.clientY,
        ctrlKey: original.ctrlKey,
        shiftKey: original.shiftKey,
        altKey: original.altKey,
        metaKey: original.metaKey,
        bubbles: true,
        cancelable: true,
      });
      konvaContent.dispatchEvent(wheelEvent);
    } catch (_) {}
  }, []);

  const dispatchMouseToPlayer = useCallback((type, originalEvt) => {
    try {
      const underlying = document.querySelector('.video-player-stage');
      if (!underlying) return;
      const konvaContent = underlying.querySelector('.konvajs-content') || underlying;
      const mouseEvent = new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        clientX: originalEvt.clientX,
        clientY: originalEvt.clientY,
        button: originalEvt.button,
        buttons: originalEvt.buttons,
        ctrlKey: originalEvt.ctrlKey,
        shiftKey: originalEvt.shiftKey,
        altKey: originalEvt.altKey,
        metaKey: originalEvt.metaKey,
      });
      konvaContent.dispatchEvent(mouseEvent);
    } catch (_) {}
  }, []);

  const handleMouseDownForward = useCallback((e) => {
    const evt = e.evt;
    if (!evt) return;
    // Right button or Ctrl+Left should pan the underlying VideoPlayer
    if (evt.button === 2 || (evt.button === 0 && evt.ctrlKey)) {
      evt.preventDefault();
      evt.stopPropagation();
      forwardingDragRef.current = true;
      dispatchMouseToPlayer('mousedown', evt);
    }
  }, [dispatchMouseToPlayer]);

  const handleMouseMoveForward = useCallback((e) => {
    const evt = e.evt;
    if (!evt) return;
    if (forwardingDragRef.current) {
      evt.preventDefault();
      evt.stopPropagation();
      dispatchMouseToPlayer('mousemove', evt);
    }
  }, [dispatchMouseToPlayer]);

  const handleMouseUpForward = useCallback((e) => {
    const evt = e.evt;
    if (!evt) return;
    if (forwardingDragRef.current) {
      evt.preventDefault();
      evt.stopPropagation();
      forwardingDragRef.current = false;
      dispatchMouseToPlayer('mouseup', evt);
    }
  }, [dispatchMouseToPlayer]);

  const handleContextMenu = useCallback((e) => {
    if (e.evt) {
      e.evt.preventDefault();
      e.evt.stopPropagation();
    }
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "calc(100% - 36px)",
        pointerEvents: isEditing ? "auto" : "none",
        zIndex: isEditing ? 10 : 1,
        border: isEditing ? "2px solid #007bff" : "none",
        borderRadius: "4px",
      }}
    >
      {/* Nipple container (like FieldCanvas) */}
      <div ref={nippleRef} style={nippleOptions} />
      <Stage
        ref={stageRef}
        width={metrics.videoDomWidth || 1}
        height={metrics.videoDomHeight || 1}
        scaleX={transform.stageScale || 1}
        scaleY={transform.stageScale || 1}
        x={transform.stageX || 0}
        y={transform.stageY || 0}
        onWheel={handleWheelForwardToPlayer}
        onMouseDown={handleMouseDownForward}
        onMouseMove={handleMouseMoveForward}
        onMouseUp={handleMouseUpForward}
        onContextMenu={handleContextMenu}
        style={{
          cursor: isEditing ? "pointer" : "default",
        }}
      >
        <Layer ref={layerRef}>
          {/* Exercise zones are now hidden when not editing */}

          {/* Draw current editing zone rectangle */}
          {container && isEditing && currentZone.length === 2 && (
            <Group>
              <Line
                points={[
                  toAbsX(currentZone[0].x),
                  toAbsY(currentZone[0].y),
                  toAbsX(currentZone[1].x),
                  toAbsY(currentZone[0].y),
                  toAbsX(currentZone[1].x),
                  toAbsY(currentZone[1].y),
                  toAbsX(currentZone[0].x),
                  toAbsY(currentZone[1].y),
                  toAbsX(currentZone[0].x),
                  toAbsY(currentZone[0].y),
                ]}
                stroke="#ff0000"
                strokeWidth={2}
                closed={true}
                fill="rgba(255, 0, 0, 0.1)"
              />
            </Group>
          )}

          {/* Draw all field points */}
          {container &&
            allPoints.map((point) => {
              const isSelected = selectedPoints.includes(point.id);
              const isInCurrentZone = currentZone.some(
                (p) => p.id === point.id
              );

              return (
                <Group
                  key={point.id}
                  draggable={isEditing}
                  onDragStart={(e) => handlePointDragStart(point.id, e)}
                  onDragMove={(e) => handlePointDragMove(point.id, e)}
                  onDragEnd={(e) => handlePointDragEnd(point.id, e)}
                  onClick={() => handlePointClick(point.id)}
                >
                  <Circle
                    x={toAbsX(point.x)}
                    y={toAbsY(point.y)}
                    radius={actualRadius / 3}
                    fill={
                      active === point.id
                        ? "white"
                        : isSelected
                        ? "#ff0000"
                        : point.color
                    }
                    stroke={isSelected ? "#ffffff" : "#ffffff"}
                    strokeWidth={isSelected ? 2 : 1}
                    shadowColor="white"
                    shadowOffset={{ x: 1, y: 1 }}
                    shadowBlur={10}
                    shadowOpacity={active === point.id ? 1 : 0}
                    style={{ cursor: isEditing ? "pointer" : "default" }}
                  />
                  <Text
                    x={toAbsX(point.x) - actualRadius * 2 - 15}
                    y={toAbsY(point.y) + actualRadius * 2}
                    text={point.desc}
                    fontSize={10}
                    fill="#ffffff"
                    stroke="#000000"
                    strokeWidth={1}
                    opacity={isEditing ? 1.0 : 0.0}
                  />
                </Group>
              );
            })}

          {/* Active point text (like FieldCanvas) */}
          {activePoint && (
            <Text
              x={toAbsX(0.01)}
              y={toAbsY(0.025)}
              text={`Active point: ${activePoint.desc}`}
              fill="white"
              fontSize={14}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default memo(ExerciseCanvas);
